import debounce from 'lodash/debounce';
import {
  type MaybeRefOrGetter,
  nextTick,
  onBeforeUnmount,
  onMounted,
  type ShallowRef,
  shallowRef,
  toValue,
  watch,
} from 'vue';
import getCssVariable from '@/shared/utils/get-css-variable';
import remToPx from '@/shared/utils/rem-to-px';
import setCssVariable from '@/shared/utils/set-css-variable';
import type { LegacyGalleryItem } from './legacyGalleryTypes';

const SCROLL_SAFE_GAP = 50;
const GRID_PAGE_SIZE = 4;
const SCROLL_DEBOUNCE_MS = 100;

export interface UseLegacyCarouselPagerOptions<T extends LegacyGalleryItem> {
  actualItemsPerSlide: MaybeRefOrGetter<number>;
  isGridMode: MaybeRefOrGetter<boolean>;
  items: MaybeRefOrGetter<readonly T[]>;
  onSwitchSlide?: (
    delta: number,
    firstVisibleItemIndex: number,
    visibleItems: Array<T | undefined>
  ) => void;
}

export interface LegacyCarouselPager<T extends LegacyGalleryItem> {
  canScroll: () => boolean;
  container: ShallowRef<HTMLElement | null>;
  getFirstVisibleItemIndex: () => number;
  getItemRenderKey: (item: T, index: number) => string;
  getItemWidth: () => number;
  getVisibleItemUuids: () => Array<string | undefined>;
  hideNextArrow: ShallowRef<boolean>;
  hidePrevArrow: ShallowRef<boolean>;
  initialize: () => void;
  isAtEnd: () => boolean;
  isItemVisible: (uuid: string) => boolean;
  itemElements: ShallowRef<Array<HTMLElement | null>>;
  leave: () => void;
  onScroll: () => void;
  refreshItems: () => void;
  scrollToItem: (uuid: string) => void;
  scrollLeft: ShallowRef<number>;
  scrollable: ShallowRef<HTMLElement | null>;
  switchSlide: (delta: number) => void;
  updateArrowVisibility: () => void;
  visibleItemUuids: ShallowRef<Array<string | undefined>>;
}

export function useLegacyCarouselPager<T extends LegacyGalleryItem>(
  options: UseLegacyCarouselPagerOptions<T>
): LegacyCarouselPager<T> {
  const container = shallowRef<HTMLElement | null>(null);
  const scrollable = shallowRef<HTMLElement | null>(null);
  const itemElements = shallowRef<Array<HTMLElement | null>>([]);
  const hidePrevArrow = shallowRef(true);
  const hideNextArrow = shallowRef(true);
  const visibleItemUuids = shallowRef<Array<string | undefined>>([]);
  let attachedScrollable: HTMLElement | null = null;
  let isUnmounted = false;
  const scrollLeft = shallowRef(0);
  let arrowVisibilityRaf: number | null = null;
  let reorderRaf: number | null = null;
  let scrollToItemRaf: number | null = null;

  function readItems(): readonly T[] {
    return toValue(options.items);
  }

  function readActualItemsPerSlide(): number {
    return toValue(options.actualItemsPerSlide);
  }

  function readIsGridMode(): boolean {
    return toValue(options.isGridMode);
  }

  function readGridColumnCount(): number {
    return readIsGridMode() ? 2 : 1;
  }

  function getItemByUuid(uuid: string | undefined): T | undefined {
    return readItems().find((item) => item.uuid === uuid);
  }

  function getItemWidth(): number {
    if (!container.value) {
      return 1;
    }
    const itemWidthRem = getCssVariable(
      '--ImageGallery--item-width',
      container.value,
      true
    );
    const itemGapRem = getCssVariable(
      '--ImageGallery--item-gap',
      container.value,
      true
    );
    const totalPx = remToPx(itemWidthRem) + remToPx(itemGapRem);
    return totalPx > 0 ? totalPx : 1;
  }

  function getFirstVisibleItemIndex(): number {
    const currentScrollable = scrollable.value;
    if (!currentScrollable) {
      return 0;
    }
    if (readIsGridMode()) {
      const pageWidth = currentScrollable.clientWidth;
      if (pageWidth <= 0) {
        return 0;
      }
      const page = Math.round(currentScrollable.scrollLeft / pageWidth);
      return page * readActualItemsPerSlide();
    }
    return Math.round(currentScrollable.scrollLeft / getItemWidth());
  }

  function getVisibleItemUuids(): Array<string | undefined> {
    const firstVisibleItemIndex = getFirstVisibleItemIndex();
    return readItems()
      .slice(
        firstVisibleItemIndex,
        firstVisibleItemIndex + readActualItemsPerSlide()
      )
      .map((item) => item.uuid);
  }

  function updateArrowVisibility(): void {
    if (arrowVisibilityRaf !== null) {
      cancelAnimationFrame(arrowVisibilityRaf);
    }
    arrowVisibilityRaf = requestAnimationFrame(() => {
      arrowVisibilityRaf = null;
      if (isUnmounted) {
        return;
      }
      const currentScrollable = scrollable.value;
      if (!currentScrollable) {
        hidePrevArrow.value = true;
        hideNextArrow.value = true;
        return;
      }

      const notScrollable =
        currentScrollable.scrollWidth <= currentScrollable.clientWidth;
      const isScrolledToBeginning =
        currentScrollable.scrollLeft <= SCROLL_SAFE_GAP;
      const isScrolledToEnd =
        currentScrollable.scrollLeft +
          currentScrollable.offsetWidth +
          SCROLL_SAFE_GAP >=
        currentScrollable.scrollWidth;

      hidePrevArrow.value = notScrollable || isScrolledToBeginning;
      hideNextArrow.value = notScrollable || isScrolledToEnd;
    });
  }

  function updateItemCount(count: number): void {
    if (isUnmounted || !container.value) {
      return;
    }
    setCssVariable(
      '--ImageGallery--js-total-item-count',
      count,
      null,
      container.value
    );
    setCssVariable(
      '--ImageGallery--js-grid-count',
      Math.ceil(count / GRID_PAGE_SIZE) || 1,
      null,
      container.value
    );
    visibleItemUuids.value = getVisibleItemUuids();
    updateArrowVisibility();
  }

  function initialize(): void {
    if (isUnmounted || !container.value) {
      return;
    }
    setCssVariable(
      '--ImageGallery--js-visible-item-count',
      readActualItemsPerSlide(),
      null,
      container.value
    );
    setCssVariable(
      '--ImageGallery--js-grid-columns',
      readGridColumnCount(),
      null,
      container.value
    );
    updateItemCount(readItems().length);
  }

  function refreshItems(): void {
    updateItemCount(readItems().length);
    updateArrowVisibility();
  }

  function preserveScrollAfterReorder(): void {
    const currentScrollable = scrollable.value;
    if (!currentScrollable) {
      return;
    }
    const savedScrollLeft = currentScrollable.scrollLeft;
    currentScrollable.style.scrollSnapType = 'none';
    void nextTick(() => {
      const nextScrollable = scrollable.value;
      if (isUnmounted || !nextScrollable) {
        return;
      }
      nextScrollable.scrollLeft = savedScrollLeft;
      if (reorderRaf !== null) {
        cancelAnimationFrame(reorderRaf);
      }
      reorderRaf = requestAnimationFrame(() => {
        reorderRaf = null;
        if (!isUnmounted && scrollable.value) {
          scrollable.value.style.scrollSnapType = '';
        }
      });
    });
  }

  function onScroll(): void {
    const currentScrollable = scrollable.value;
    if (isUnmounted || !currentScrollable) {
      return;
    }

    const firstVisibleItemIndex = getFirstVisibleItemIndex();
    const currentlyVisibleItemUuids = getVisibleItemUuids();
    if (currentlyVisibleItemUuids.join() !== visibleItemUuids.value.join()) {
      visibleItemUuids.value = currentlyVisibleItemUuids;
      const visibleItems = currentlyVisibleItemUuids.map(getItemByUuid);
      const delta =
        currentScrollable.scrollLeft - scrollLeft.value > 0 ? 1 : -1;
      options.onSwitchSlide?.(delta, firstVisibleItemIndex, visibleItems);
    }

    scrollLeft.value = currentScrollable.scrollLeft;
    updateArrowVisibility();
  }

  const debouncedOnScroll = debounce(onScroll, SCROLL_DEBOUNCE_MS);

  function switchSlide(delta: number): void {
    if (!scrollable.value) {
      return;
    }
    scrollable.value.scrollLeft += scrollable.value.clientWidth * delta;
  }

  function scrollToItem(uuid: string): void {
    if (scrollToItemRaf !== null) {
      cancelAnimationFrame(scrollToItemRaf);
    }
    scrollToItemRaf = requestAnimationFrame(() => {
      scrollToItemRaf = null;
      if (isUnmounted) {
        return;
      }
      const index = readItems().findIndex((item) => item.uuid === uuid);
      const item = index === -1 ? null : itemElements.value[index];
      item?.scrollIntoView({ inline: 'start', block: 'nearest' });
    });
  }

  function canScroll(): boolean {
    return scrollable.value
      ? scrollable.value.scrollWidth > scrollable.value.clientWidth
      : false;
  }

  function isItemVisible(uuid: string): boolean {
    const currentScrollable = scrollable.value;
    if (!currentScrollable) {
      return false;
    }
    const index = readItems().findIndex((item) => item.uuid === uuid);
    const item = index === -1 ? null : itemElements.value[index];
    if (!item) {
      return false;
    }
    const itemRect = item.getBoundingClientRect();
    const scrollableRect = currentScrollable.getBoundingClientRect();
    return (
      itemRect.left >= scrollableRect.left &&
      itemRect.right <= scrollableRect.right
    );
  }

  function isAtEnd(): boolean {
    if (!scrollable.value) {
      return true;
    }
    return (
      scrollable.value.scrollLeft + scrollable.value.clientWidth >=
      scrollable.value.scrollWidth - 10
    );
  }

  function getItemRenderKey(item: T, index: number): string {
    return item.renderKey || item.uuid || `gallery-item-${index}`;
  }

  function leave(): void {
    isUnmounted = true;
    if (arrowVisibilityRaf !== null) {
      cancelAnimationFrame(arrowVisibilityRaf);
      arrowVisibilityRaf = null;
    }
    if (reorderRaf !== null) {
      cancelAnimationFrame(reorderRaf);
      reorderRaf = null;
    }
    if (scrollToItemRaf !== null) {
      cancelAnimationFrame(scrollToItemRaf);
      scrollToItemRaf = null;
    }
    attachedScrollable?.removeEventListener('scroll', debouncedOnScroll);
    attachedScrollable = null;
    debouncedOnScroll.cancel();
  }

  watch(
    () => toValue(options.items),
    (newItems, oldItems) => {
      if (!oldItems || newItems.length !== oldItems.length) {
        updateItemCount(newItems.length);
        return;
      }
      preserveScrollAfterReorder();
    },
    { deep: false }
  );

  watch(
    [
      (): number => toValue(options.actualItemsPerSlide),
      (): boolean => toValue(options.isGridMode),
      (): number => readGridColumnCount(),
    ],
    initialize
  );

  onMounted(() => {
    isUnmounted = false;
    attachedScrollable = scrollable.value;
    attachedScrollable?.addEventListener('scroll', debouncedOnScroll);
    void nextTick(initialize);
  });

  onBeforeUnmount(leave);

  return {
    canScroll,
    container,
    getFirstVisibleItemIndex,
    getItemRenderKey,
    getItemWidth,
    getVisibleItemUuids,
    hideNextArrow,
    hidePrevArrow,
    initialize,
    isAtEnd,
    isItemVisible,
    itemElements,
    leave,
    onScroll,
    refreshItems,
    scrollToItem,
    scrollLeft,
    scrollable,
    switchSlide,
    updateArrowVisibility,
    visibleItemUuids,
  };
}
